import { ReactNode } from 'react';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import DateRangeBadge from 'components/shared/DateRangeBadge';
import Foldout from 'components/shared/Foldout';
import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import ArrowLeft from 'svg/ArrowLeft';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  subtitleReady?: boolean;
  startDate?: Date;
  endDate?: Date;
  children: ReactNode;
};

export default function SlideoutShell({
  open,
  onClose,
  title,
  subtitle,
  subtitleReady = true,
  startDate,
  endDate,
  children,
}: Props) {
  const isMobile = useIsMobile();

  return (
    <Foldout open={open} onBackdropClick={onClose} width={isMobile ? '100%' : 'min(95%, 960px)'}>
      <Container>
        <HeaderBar>
          {isMobile && (
            <BackButton type='button' aria-label='Back' onClick={onClose}>
              <ArrowLeft />
            </BackButton>
          )}
          <TitleSection>
            <TitleRow>
              <Title>{title}</Title>
              {!isMobile && startDate && endDate && <DateRangeBadge startDate={startDate} endDate={endDate} />}
            </TitleRow>
            {subtitle !== undefined && (
              <Subtitle>
                {subtitleReady ? subtitle : <CircularProgress size={12} thickness={2} color={colors.blue100} />}
              </Subtitle>
            )}
          </TitleSection>
          <HeaderActions>
            <CloseButton type='button' aria-label='Close' onClick={onClose}>
              {'\u00D7'}
            </CloseButton>
          </HeaderActions>
        </HeaderBar>
        <Body>{children}</Body>
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin: 2px 0 0 -8px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.black75};
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: ${({ theme }) => theme.colors.black100};
`;

const Subtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  min-height: 16px;
  color: ${({ theme }) => theme.colors.black50};
  display: flex;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.black75};
  padding: 4px 8px;
`;

export const BorderedSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 24px;

  @media (max-width: 800px) {
    padding: 0;
    border: none;
    border-radius: 0;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 800px) {
    padding: 16px;
    gap: 16px;
  }
`;
